import React from "react";
import { TextField } from "@mui/material";
import { ClusterType, displayClusterType, displayPageType, PageType } from "Backend";
import StyledAutocomplete from "./StyledAutocomplete";

export interface SectionSelectProps {
  sectionOptions?: SectionOption[];
  pageTypes: PageType[] | undefined;
  setPageTypes: (values: PageType[] | undefined) => void;
  clusterTypes: ClusterType[] | undefined;
  setClusterTypes: (values: ClusterType[] | undefined) => void;
  pageLabels: string[] | undefined;
  setPageLabels: (values: string[] | undefined) => void;
  overrideLabel?: string;
}

export type SectionOption =
  | { key: ClusterType; type: "cluster_type"; count?: number }
  | { key: PageType; type: "page_type"; count?: number }
  | { key: string; type: "label"; count?: number };

const SectionSelect = (props: SectionSelectProps) => {
  const { pageTypes, setPageTypes, clusterTypes, setClusterTypes, pageLabels, setPageLabels, sectionOptions } = props;

  const selectedPageTypes: Array<SectionOption> = (pageTypes || []).map(
    (p: PageType) => ({ type: "page_type", key: p }) as SectionOption
  );
  const selectedClusterTypes: Array<SectionOption> = (clusterTypes || []).map((c: ClusterType) => ({
    type: "cluster_type",
    key: c,
  }));
  const selectedLabels: Array<SectionOption> = (pageLabels || []).map((l: string) => ({
    type: "label",
    key: l,
  }));

  const options: Array<SectionOption> = sectionOptions || [
    // Page Types
    { key: PageType.PathologyReport, type: "page_type" },
    { key: PageType.OperativeReport, type: "page_type" },
    { key: PageType.DeathCertificate, type: "page_type" },
    { key: PageType.LabReport, type: "page_type" },
    // Cluster Types
    { key: ClusterType.FamilyHistory, type: "cluster_type" },
    { key: ClusterType.PatientHistory, type: "cluster_type" },
  ];

  return (
    <StyledAutocomplete<SectionOption, true, false, false>
      multiple
      size="small"
      freeSolo={false}
      onChange={(_evt, values) => {
        const clusterTypes = values.filter((v) => v.type === "cluster_type").map((v) => v.key) as ClusterType[];
        const pageTypes = values.filter((v) => v.type === "page_type").map((v) => v.key) as PageType[];
        const labels = values.filter((v) => v.type === "label").map((v) => v.key) as string[];
        setClusterTypes(clusterTypes);
        setPageTypes(pageTypes);
        setPageLabels(labels);
      }}
      value={selectedPageTypes.concat(selectedClusterTypes).concat(selectedLabels)}
      isOptionEqualToValue={(t1, t2) => t1.key === t2.key}
      renderOption={(p: React.HTMLAttributes<HTMLLIElement>, opt: SectionOption) =>
        opt.type === "cluster_type" ? (
          <li {...p}>
            {displayClusterType(opt.key)}
            {opt.count ? ` - (${opt.count})` : ""}
          </li>
        ) : opt.type === "page_type" ? (
          <li {...p}>
            {displayPageType(opt.key)}
            {opt.count ? ` - (${opt.count})` : ""}
          </li>
        ) : (
          <li {...p}>
            {opt.key}
            {opt.count ? ` - (${opt.count})` : ""}
          </li>
        )
      }
      getOptionLabel={(opt) =>
        opt.type === "cluster_type"
          ? displayClusterType(opt.key)
          : opt.type === "page_type"
            ? displayPageType(opt.key)
            : opt.key
      }
      renderInput={(params) => {
        return <TextField {...params} label={props.overrideLabel || `Record Type (${options.length})`} fullWidth />;
      }}
      options={options}
    />
  );
};

export default SectionSelect;
